import { useEffect } from 'react';

/**
 * PWA installability for the kiosk surface (`BACKLOG.md` C7). Two pieces, both scoped to exactly
 * `/kiosk` (no trailing slash — the route this app actually mounts, `App.tsx`'s `path="/kiosk"`; a
 * service-worker scope only controls a page whose URL *starts with* the scope string, and `/kiosk`
 * does not start with `/kiosk/`, so the two must match exactly):
 *
 *   - `useKioskManifest` swaps the document's single `<link rel="manifest">` to a kiosk-scoped
 *     manifest (`public/kiosk-manifest.webmanifest`: `start_url`/`scope` both `/kiosk`,
 *     `display: "standalone"`) while this page is mounted, and restores whatever was there before on
 *     unmount. This app has one `index.html` for every route, so there is only one manifest link in
 *     the document at a time — a real, if unusual, working way to give one route its own
 *     installability profile without a second HTML entry point or a separate Vite build target.
 *   - `registerKioskServiceWorker` registers `public/kiosk-sw.js`, scoped to `/kiosk`.
 *
 * **What this does NOT attempt: genuine offline data entry.** `BACKLOG.md` C7's own row leaves "does
 * this need real offline capability" as a still-open, unanswered question — building a real offline
 * write queue (demographics/consent writes captured while the tablet has no connection, replayed once
 * it reconnects) is separate, unstarted work with its own real questions (conflict resolution, how a
 * short-lived kiosk token behaves once it has expired by the time connectivity returns). `kiosk-sw.js`
 * only caches the app shell for fast, resilient loading and installability; every actual API call in
 * `api/kiosk.ts` goes straight to the network, and a request made with no connectivity simply fails
 * with this page's own ordinary error handling — the same as it would with no service worker at all.
 *
 * **A launch from an installed home-screen icon carries no token** — `start_url` has no hash, and a
 * PWA's installed-icon launch never carries whatever fragment the original install-time page had.
 * `KioskCheckinPage.tsx` already renders its "no token" screen for exactly this case, which is the
 * honest behaviour: the realistic kiosk deployment is staff opening a fresh per-visit link each time
 * (`useKioskToken.ts`'s own header), not relying on a resumable installed session.
 */
const KIOSK_MANIFEST_HREF = '/kiosk-manifest.webmanifest';
const KIOSK_SERVICE_WORKER_PATH = '/kiosk-sw.js';
const KIOSK_SCOPE = '/kiosk';

export function useKioskManifest(): void {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (link === null) return undefined;
    const previousHref = link.getAttribute('href');
    link.setAttribute('href', KIOSK_MANIFEST_HREF);
    return () => {
      if (previousHref !== null) link.setAttribute('href', previousHref);
    };
  }, []);
}

/** Returns a cleanup function, matching `useEffect`'s own return shape — called directly from
 * `KioskCheckinPage.tsx`'s own `useEffect(() => registerKioskServiceWorker(), [])` rather than being
 * a hook itself, since registration (unlike the manifest swap) has nothing to restore on unmount: a
 * service worker registration is a durable, origin-scoped browser fact, not page-local state. */
export function registerKioskServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.register(KIOSK_SERVICE_WORKER_PATH, { scope: KIOSK_SCOPE }).catch(() => {
    // Installability is a progressive enhancement here, not a requirement for the check-in flow to
    // work — a registration failure (an unsupported browser mode, a disabled service-worker API, a
    // network error fetching the script) must never block check-in itself, so this is deliberately
    // swallowed rather than surfaced as a page-level error.
  });
}
